// StatusDropdown : Dropdown intelligent pour les transitions de statut de chantier
// Affiche uniquement les transitions autorisées selon le statut actuel et les dates

import { useState } from 'react';
import { ChevronDown, AlertTriangle, Info, Lock } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  StatutChantier,
  STATUTS_CONFIG,
  getTransitionsPossibles,
  TransitionOption,
} from '@/constants/statuts';
import { cn } from '@/lib/utils';

interface StatusDropdownProps {
  // Statut actuel du chantier
  statutActuel: StatutChantier;
  // Dates pour la logique de transition
  dateDebutPrevue?: string | null;
  dateDebutReelle?: string | null;
  dateFinPrevue?: string | null;
  dateFinReelle?: string | null;
  // Callback quand une transition est sélectionnée
  onTransition: (nouveauStatut: StatutChantier, modal?: string) => void;
  // Désactiver le dropdown (ex: pendant un chargement)
  disabled?: boolean;
  // Classe CSS additionnelle
  className?: string;
}

export function StatusDropdown({
  statutActuel,
  dateDebutPrevue,
  dateDebutReelle,
  dateFinPrevue,
  dateFinReelle,
  onTransition,
  disabled = false,
  className,
}: StatusDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Obtenir la config du statut actuel
  const statutConfig = STATUTS_CONFIG[statutActuel] || STATUTS_CONFIG.brouillon;

  // Obtenir les transitions possibles
  const transitions = getTransitionsPossibles(
    statutActuel,
    dateDebutPrevue,
    dateDebutReelle,
    dateFinPrevue,
    dateFinReelle
  );

  // Gérer le clic sur une option
  const handleSelect = (option: TransitionOption) => {
    if (option.disabled) return;
    setIsOpen(false);
    onTransition(option.statut, option.modal);
  };

  // Si aucune transition possible (ex: archivé), afficher juste le badge
  if (transitions.length === 0) {
    return (
      <div
        className={cn(
          'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-sm font-medium',
          statutConfig.color,
          className
        )}
      >
        <span>{statutConfig.icon}</span>
        <span>{statutConfig.label}</span>
        <Lock className="h-3 w-3 ml-1 opacity-50" />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            disabled={disabled}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 h-auto text-sm font-medium hover:opacity-90 transition-opacity',
              statutConfig.color,
              disabled && 'opacity-50 cursor-not-allowed',
              className
            )}
          >
            <span>{statutConfig.icon}</span>
            <span>{statutConfig.label}</span>
            <ChevronDown className="h-3.5 w-3.5 ml-0.5 opacity-70" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="start" className="w-64">
          <DropdownMenuLabel className="text-xs text-muted-foreground">
            Changer le statut
          </DropdownMenuLabel>
          <DropdownMenuSeparator />

          {transitions.map((option) => (
            <Tooltip key={option.statut} delayDuration={300}>
              <TooltipTrigger asChild>
                <DropdownMenuItem
                  onClick={() => handleSelect(option)}
                  disabled={option.disabled}
                  className={cn(
                    'flex flex-col items-start gap-0.5 py-2 cursor-pointer',
                    option.disabled && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  <div className="flex items-center gap-2 w-full">
                    {/* Icône du statut cible */}
                    <span className="text-base">
                      {STATUTS_CONFIG[option.statut]?.icon || '📋'}
                    </span>
                    {/* Label de la transition */}
                    <span className="font-medium flex-1">{option.label}</span>
                    {/* Indicateurs */}
                    {option.warning && (
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                    )}
                    {option.modal && (
                      <Info className="h-3.5 w-3.5 text-muted-foreground" />
                    )}
                  </div>
                  {/* Description */}
                  <span className="text-xs text-muted-foreground pl-7">
                    {option.description}
                  </span>
                </DropdownMenuItem>
              </TooltipTrigger>
              {/* Tooltip pour warning ou raison de désactivation */}
              {(option.warning || option.disabledReason) && (
                <TooltipContent side="right" className="max-w-xs">
                  {option.warning && (
                    <div className="flex items-start gap-2 text-amber-500">
                      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                      <span>{option.warning}</span>
                    </div>
                  )}
                  {option.disabledReason && (
                    <div className="flex items-start gap-2 text-muted-foreground">
                      <Lock className="h-4 w-4 shrink-0 mt-0.5" />
                      <span>{option.disabledReason}</span>
                    </div>
                  )}
                </TooltipContent>
              )}
            </Tooltip>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </TooltipProvider>
  );
}

export default StatusDropdown;
